import { Card, Row, Col, Form, Badge, Button } from 'react-bootstrap';

// An execution that is a reel off the shelf, not a run this launch will make.
//
// There is nothing to choose on it: the machine is PHANTOM and the length is
// whatever was taken from the reel, so both fields are locked and the card says
// where it came from instead of asking for anything.
//
// It exists as a component because the wizard drew it in THREE places already —
// the conductor cards, the flat execution list and the pre-stranding list — and
// a fourth was missing it entirely: a reduced-earth cable's per-rope list has
// its own renderer, which knew nothing about phantoms, so a reel used on
// `CYYF 3x25+16` added itself correctly and then drew as an ORDINARY card with
// an empty machine and an empty length. Reported 2026-10-22: "se adauga bine …
// dar imi creaza card gol de executie, nu se creaza card de phantom".
//
// ── …AND A CLAIM ON ROPE THAT IS LAUNCHED BUT NOT WOUND ───────────────────
//
// Rope already in a machine's queue can be spent too
// (helpers/ropeClaimSelection.js), and that execution has even less to choose
// on it: it schedules NOTHING. The drum is already being wound by the job that
// launched it; this row only says that part of its output has a customer. It
// arrived drawn as an ordinary editable card — *"imi pune card clasic de
// executie. Trebuie tot card de phantom! si la cel activ si la cel not made
// yet."* (2026-11-04) — which is the same bug this component was extracted to
// stop, one source along.
//
// Same card, two sources, and the difference is WHEN. A phantom's metres exist
// and were measured; a claim's metres are owed. So: amber instead of cyan, and
// the machine field reads CLAIMED rather than PHANTOM — the drum's own line is
// named in the detail row, where it belongs, because it is the donor's machine
// and not this execution's.
//
// @param exec     the execution row — `isPhantomSource` + `phantomId`, or
//                 `isRopeClaim` + `ropeClaim`
// @param onRemove called with no arguments; the caller knows its own indices
// @param warning  optional <ExecWarning/>, which needs the wizard's own context
const PhantomExecutionCard = ({ exec, onRemove, warning = null }) => {
  const share = !!exec.isRopeClaim && !!exec.ropeClaim?.tokenId;
  const running = String(exec.claimStatus || '').toUpperCase() === 'ACTIVE';
  const machine = String(exec.claimMachineId || '').toUpperCase();

  return (
  <Card
    className={`${share ? 'border-warning' : 'border-info'} mb-2 shadow-sm`}
    style={{ borderLeft: `5px solid ${share ? '#ffc107' : '#0dcaf0'}` }}
  >
    <Card.Header
      className={`${
        share ? 'bg-warning-subtle' : 'bg-info-subtle'
      } d-flex justify-content-between align-items-center`}
    >
      <div className="d-flex align-items-center gap-2">
        {share ? (
          <Badge bg={running ? 'warning' : 'secondary'}>
            {running ? `CLAIMED · ● ON ${machine || 'LINE'}` : 'CLAIMED · QUEUED'}
          </Badge>
        ) : (
          <Badge bg="info">PHANTOM</Badge>
        )}
        <span className="fw-semibold">
          {Number(exec.plannedLength || 0).toLocaleString()} m
        </span>
        {!share && (exec.phantomColor || exec.color) && (
          <Badge bg="dark">{exec.phantomColor || exec.color}</Badge>
        )}
        <span className="text-muted fs-10 font-monospace">
          {share ? exec.ropeClaim.tokenId : exec.phantomId}
        </span>
      </div>
      {onRemove && (
        <Button variant="outline-danger" size="sm" onClick={() => onRemove()}>
          Remove
        </Button>
      )}
    </Card.Header>

    <Card.Body className="py-2">
      <Row className="g-2">
        <Col md={6}>
          <Form.Group>
            <Form.Label>Machine{warning}</Form.Label>
            <Form.Control
              size="sm"
              // CLAIMED, not the donor's machine: this execution schedules
              // nothing, and naming a line here would read as a run that is
              // going to happen. The drum's own line is in the detail row.
              value={share ? 'CLAIMED' : 'PHANTOM'}
              readOnly
              disabled
              className={`bg-light fw-semibold ${share ? 'text-warning' : 'text-info'}`}
            />
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group>
            <Form.Label>
              Length (m) — {share ? 'claimed off that drum' : 'from phantom'}
            </Form.Label>
            <Form.Control
              size="sm"
              type="number"
              value={exec.plannedLength || ''}
              disabled
              className="bg-light"
            />
          </Form.Group>
        </Col>
      </Row>

      {/* WHOSE drum it is, and that nothing here is being scheduled. The whole
          reason this model replaced the share one is that the rope was already
          launched — so the card says who is winding it rather than implying
          this product will. */}
      {share && (
        <div className="fs-10 mt-1">
          <div className="text-muted">
            {exec.claimWireType && (
              <span className="me-2">{String(exec.claimWireType).toUpperCase()}</span>
            )}
            {exec.claimSection != null && <span className="me-2">sect. {exec.claimSection}</span>}
            {exec.claimDrumPlanned > 0 && (
              <span>
                of a {Number(exec.claimDrumPlanned).toLocaleString()} m drum
                {machine ? ` on ${machine}` : ''}
                {running && exec.claimDrumProduced > 0 && (
                  <> · {Number(exec.claimDrumProduced).toLocaleString()} m wound</>
                )}
              </span>
            )}
          </div>
          <div className="text-700">
            {exec.claimLabel || 'another lot'} is winding it
            {exec.claimJobCode ? ` · ${exec.claimJobCode}` : ''} — nothing is added to the
            queue for this row.
          </div>
        </div>
      )}

      {/* What the reel is, when it says. A stock reel of 1×Ø1.35 + 6×Ø1.7 and
          one of 1×Ø1.7 + 6×Ø2.14 are different conductors, and on a cable that
          is stranded twice the whole question is which rope this one feeds. */}
      {(exec.phantomSection || exec.phantomWireDiameter) && (
        <div className="text-muted fs-10 mt-1">
          {exec.phantomWireType && (
            <span className="me-2">{String(exec.phantomWireType).toUpperCase()}</span>
          )}
          {Array.isArray(exec.phantomWireDiameter) && exec.phantomWireDiameter.length > 0 && (
            <span className="me-2">
              {Array.isArray(exec.phantomNumberOfWires) &&
              exec.phantomNumberOfWires.length === exec.phantomWireDiameter.length
                ? exec.phantomWireDiameter
                    .map((d, i) => `${exec.phantomNumberOfWires[i]}×Ø${d}`)
                    .join(' + ')
                : `Ø${exec.phantomWireDiameter.join(', ')}`}
            </span>
          )}
          {exec.phantomSection != null && <span>sect. {exec.phantomSection}</span>}
        </div>
      )}

      {exec.phantomNote && (
        <div className="text-muted fs-10 mt-2 fst-italic">{exec.phantomNote}</div>
      )}
    </Card.Body>
  </Card>
  );
};

export default PhantomExecutionCard;
